// Grace period of the forced password change. The API sends the first day on
// which the change is mandatory (user.password_change_deadline, YYYY-MM-DD,
// Israel time) and starts answering 423 from then; until then the popup is a
// dismissible reminder shown once a day.

const TZ = "Asia/Jerusalem";
export const REMINDER_DISMISSED_KEY = "force-password-reminder-dismissed";

// Today's date in Israel as YYYY-MM-DD (en-CA formats that way), whatever the device's timezone.
export function todayInIsrael() {
  return new Date().toLocaleDateString("en-CA", { timeZone: TZ });
}

// No deadline (or an old API that does not send one) = mandatory right away.
export function isEnforced(deadline) {
  return !deadline || todayInIsrael() >= deadline;
}

// Whole days from today (Israel) to the deadline day: 7 a week ahead, 0 on the
// deadline itself, negative after it. null without a deadline. For analytics.
export function daysUntil(deadline) {
  if (!deadline) return null;
  const day = 24 * 60 * 60 * 1000;
  return Math.round(
    (Date.parse(`${deadline}T00:00:00Z`) -
      Date.parse(`${todayInIsrael()}T00:00:00Z`)) /
      day
  );
}

export function reminderDismissedToday() {
  try {
    return localStorage.getItem(REMINDER_DISMISSED_KEY) === todayInIsrael();
  } catch (e) {
    return false;
  }
}

export function rememberReminderDismissed() {
  try {
    localStorage.setItem(REMINDER_DISMISSED_KEY, todayInIsrael());
  } catch (e) {
    // private mode: the reminder just shows again on the next page load
  }
}

const GEMATRIA = [
  [400, "ת"], [300, "ש"], [200, "ר"], [100, "ק"], [90, "צ"], [80, "פ"],
  [70, "ע"], [60, "ס"], [50, "נ"], [40, "מ"], [30, "ל"], [20, "כ"],
  [10, "י"], [9, "ט"], [8, "ח"], [7, "ז"], [6, "ו"], [5, "ה"],
  [4, "ד"], [3, "ג"], [2, "ב"], [1, "א"],
];

// 18 -> י״ח, 787 -> תשפ״ז (15 and 16 are written ט״ו / ט״ז).
function gematria(number) {
  let letters = "";
  let rest = number;
  while (rest > 0) {
    if (rest === 15) { letters += "טו"; break; }
    if (rest === 16) { letters += "טז"; break; }
    const [value, letter] = GEMATRIA.find(([v]) => v <= rest);
    letters += letter;
    rest -= value;
  }
  return letters.length > 1
    ? letters.slice(0, -1) + "״" + letters.slice(-1)
    : letters + "׳";
}

// "29.10.26 (י״ח בחשוון)": the civil date first, the Hebrew date in brackets.
// Only the parts (day number, month name) come from Intl: its dateStyle output
// for the Hebrew calendar is broken in some browsers, and its numerals are
// digits, so the letters are written here.
export function deadlineShortLabel(deadline) {
  if (!deadline) return "";
  const [year, month, day] = deadline.split("-");
  const civil = `${day}.${month}.${year.slice(-2)}`;
  try {
    // Noon UTC is the same calendar day in Israel all year round.
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("he-IL", {
        calendar: "hebrew",
        day: "numeric",
        month: "long",
        timeZone: TZ,
      })
        .formatToParts(new Date(`${deadline}T12:00:00Z`))
        .map((p) => [p.type, p.value])
    );
    const hebrewDay = parseInt(parts.day, 10);
    if (!hebrewDay || !parts.month) return civil;
    return `${civil} (${gematria(hebrewDay)} ב${parts.month})`;
  } catch (e) {
    return civil;
  }
}
